import { NumberInput } from '@core/components/Inputs';
import { SelectAsyncInput } from '@core/components/Select';
import {
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  makeStyles,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@material-ui/core';
import SelectCstClassificacaoTributaria from 'app/components/Select/SelectCstClassificacaoTributaria';
import SelectCstSituacaoTributaria from 'app/components/Select/SelectCstSituacaoTributaria';
import cestApi from 'app/services/api/servicoFiscal/cestApi';
import cfopApi from 'app/services/api/servicoFiscal/cfopApi';
import ncmApi from 'app/services/api/servicoFiscal/ncmApi';
import origemProdutoApi from 'app/services/api/servicoFiscal/origemProdutoApi';
import { useFormikContext } from 'formik';
import React from 'react';
import DadosFiscaisIcms from './DadosFiscaisIcms';
import DadosFiscaisIpi from './DadosFiscaisIpi';

export const useStyles = makeStyles({
  container: {
    display: 'inline-block',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
});

const onlyNumberRegex = /^\d*$/;

const maximoDigitosEsquerda = (values, limit) => {
  return !values.floatValue || values.floatValue.toString().split('.')[0].length <= limit;
};

const maximo9DigitosEsquerda = (values) => {
  return maximoDigitosEsquerda(values, 9);
};

const maximo10DigitosEsquerda = (values) => {
  return maximoDigitosEsquerda(values, 10);
};

const TabDadosFiscaisProduto = ({
  camposMedidas = true,
  esconderCamposIcms = false,
  isAtualizarAliquota = false,
  isVenda = false,
  empresaIbsCbs = false,
}) => {
  const formik = useFormikContext();
  const styles = useStyles();

  const { errors, touched, submitCount } = formik;

  const _errors = {
    fiscal: {
      pesoLiquido: (submitCount || touched?.fiscal?.pesoLiquido) && errors?.fiscal?.pesoLiquido,
      pesoBruto: (submitCount || touched?.fiscal?.pesoBruto) && errors?.fiscal?.pesoBruto,
      altura: (submitCount || touched?.fiscal?.altura) && errors?.fiscal?.ltura,
      largura: (submitCount || touched?.fiscal?.largura) && errors?.fiscal?.largura,
      profundidade: (submitCount || touched?.fiscal?.profundidade) && errors?.fiscal?.profundidade,
      origemProduto: (submitCount || touched?.fiscal?.origemProduto) && errors?.fiscal?.origemProduto,
      numeroPedidoCompra: (submitCount || touched?.fiscal?.numeroPedidoCompra) && errors?.fiscal?.numeroPedidoCompra,
      numeroItemPedidoCompra:
        (submitCount || touched?.fiscal?.numeroItemPedidoCompra) && errors?.fiscal?.numeroItemPedidoCompra,
      cstSituacaoTributaria:
        (submitCount || touched?.fiscal?.cstSituacaoTributaria) && errors?.fiscal?.cstSituacaoTributaria,
      cstClassificacaoTributaria:
        (submitCount || touched?.fiscal?.cstClassificacaoTributaria) && errors?.fiscal?.cstClassificacaoTributaria,
      valorIbs: (submitCount || touched?.fiscal?.valorIbs) && errors?.fiscal?.valorIbs,
      valorCbs: (submitCount || touched?.fiscal?.valorCbs) && errors?.fiscal?.valorCbs,
      valorIs: (submitCount || touched?.fiscal?.valorIs) && errors?.fiscal?.valorIs,
    },
  };

  const warnPesoBruto =
    formik.values?.fiscal?.pesoLiquido !== null &&
    formik.values?.fiscal?.pesoLiquido !== undefined &&
    formik.values?.fiscal?.pesoBruto !== null &&
    formik.values?.fiscal?.pesoBruto !== undefined &&
    formik.values?.fiscal?.pesoLiquido > formik.values?.fiscal?.pesoBruto;

  const campoCstClassificacaoTributaria = () => {
    const situacaoTributariaPreenchida = !!formik.values?.fiscal?.cstSituacaoTributaria?.id;

    const selectCstClassificacao = (
      <SelectCstClassificacaoTributaria
        id="fiscal.cstClassificacaoTributaria"
        name="fiscal.cstClassificacaoTributaria"
        label="Código de classificação tributária"
        value={formik.values?.fiscal?.cstClassificacaoTributaria}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        disabled={!situacaoTributariaPreenchida}
        idSituacao={formik.values?.fiscal?.cstSituacaoTributaria?.idSituacao}
        touched={!!_errors?.fiscal?.cstClassificacaoTributaria}
        error={_errors?.fiscal?.cstClassificacaoTributaria}
        required={isVenda ? false : true}
      />
    );

    if (situacaoTributariaPreenchida) {
      return selectCstClassificacao;
    }

    return (
      <Tooltip title="O campo será habilitado quando o código de situação tributária for informada" placement="top">
        <div style={{ width: '100%' }}>{selectCstClassificacao}</div>
      </Tooltip>
    );
  };

  return (
    <Grid container spacing={3} className="mt-16">
      <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
        <SelectAsyncInput
          id="fiscal.codigoNCM"
          name="fiscal.codigoNCM"
          label="Código NCM"
          autoFocus
          value={formik.values?.fiscal?.codigoNCM}
          fetchOnFocus
          onChange={(event, value) => {
            formik.setFieldValue('fiscal.codigoNCM.id', value?.id);
            formik.setFieldValue('fiscal.codigoNCM.codigo', value?.codigo);
            formik.setFieldValue('fiscal.codigoNCM.descricao', value?.descricao);
          }}
          getOptionKey={(option) => {
            return option?.id;
          }}
          getOptionLabel={(option) => {
            return option?.descricao;
          }}
          fetchData={async (query = '', next = 0) => {
            if (typeof query === 'object') {
              query = '';
            }
            const response = await ncmApi.search({
              size: 100,
              sort: 'codigo',
              page: next,
              q: query,
            });

            const data = response?.data || [];

            return { data };
          }}
          renderOption={(option) => {
            return (
              <Tooltip title={option?.descricao}>
                <div className={styles.container} style={{ maxWidth: '100%' }}>
                  {option?.descricao}
                </div>
              </Tooltip>
            );
          }}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
        <SelectAsyncInput
          id="fiscal.cest"
          name="fiscal.cest"
          label="CEST"
          value={formik.values?.fiscal?.cest}
          fetchOnFocus
          onChange={(event, value) => {
            formik.setFieldValue('fiscal.cest.id', value?.id);
            formik.setFieldValue('fiscal.cest.codigo', value?.codigo);
            formik.setFieldValue('fiscal.cest.descricao', value?.descricao);
          }}
          getOptionKey={(option) => {
            return option?.id;
          }}
          getOptionLabel={(option) => {
            return option?.codigo && option?.descricao ? `${option.codigo} - ${option.descricao}` : '';
          }}
          fetchData={async (query = '', next = 0) => {
            if (typeof query === 'object') {
              query = '';
            }
            const response = await cestApi.search({
              size: 100,
              sort: 'codigo',
              page: next,
              q: query,
            });

            const data = response?.data || [];

            return { data };
          }}
          renderOption={(option) => {
            const labelOption = option?.codigo && option?.descricao ? `${option.codigo} - ${option.descricao}` : '';
            return (
              <Tooltip title={labelOption}>
                <div className={styles.container} style={{ maxWidth: '100%' }}>
                  {labelOption}
                </div>
              </Tooltip>
            );
          }}
        />
      </Grid>

      {camposMedidas ? (
        <>
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <SelectAsyncInput
              id="fiscal.cfop"
              name="fiscal.cfop"
              label="CFOP"
              value={formik.values?.fiscal?.cfop}
              fetchOnFocus
              onChange={(event, value) => {
                formik.setFieldValue('fiscal.cfop.id', value?.id);
                formik.setFieldValue('fiscal.cfop.codigo', value?.codigo);
                formik.setFieldValue('fiscal.cfop.descricao', value?.descricao);
              }}
              getOptionKey={(option) => {
                return option?.id;
              }}
              getOptionLabel={(option) => {
                return option?.codigo && option?.descricao ? `${option.codigo} - ${option.descricao}` : '';
              }}
              fetchData={async (query = '', next = 0) => {
                if (typeof query === 'object') {
                  query = '';
                }
                const response = await cfopApi.search({
                  size: 100,
                  sort: 'codigo',
                  page: next,
                  q: query,
                });

                const data = response?.data || [];

                return { data };
              }}
              renderOption={(option) => {
                const labelOption = option?.codigo && option?.descricao ? `${option.codigo} - ${option.descricao}` : '';
                return (
                  <Tooltip title={labelOption}>
                    <div className={styles.container} style={{ maxWidth: '100%' }}>
                      {labelOption}
                    </div>
                  </Tooltip>
                );
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={4} lg={4} xl={4}>
            <FormControl
              InputLabelProps={{
                shrink: true,
              }}
              variant="outlined"
              style={{ width: '100%' }}
              error={!!_errors.tipo}
            >
              <InputLabel id="fiscal.tipoUnidadeMedida">Unidade da medida</InputLabel>
              <Select
                id="fiscal.tipoUnidadeMedida"
                name="fiscal.tipoUnidadeMedida"
                value={formik.values?.fiscal?.tipoUnidadeMedida}
                displayEmpty
                fullWidth
                label="Unidade da medida"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              >
                <MenuItem style={{ minHeight: 33 }} value="VAZIO">
                  {' '}
                </MenuItem>
                <MenuItem value="QUILOGRAMA">Quilograma</MenuItem>
                <MenuItem value="UNIDADE">Unidade</MenuItem>
              </Select>
              <FormHelperText>{_errors?.finalidadeBloco || ''}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={4} lg={4} xl={4}>
            <NumberInput
              label="Peso líquido (kg)"
              id="fiscal.pesoLiquido"
              decimalScale={6}
              isAllowed={maximo9DigitosEsquerda}
              decimalSeparator=","
              allowNegative={false}
              value={formik.values?.fiscal?.pesoLiquido}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              touched={formik.touched.fiscal?.pesoLiquido}
              error={formik.errors.fiscal?.pesoLiquido}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={4} lg={4} xl={4}>
            <NumberInput
              label="Peso bruto (kg)"
              id="fiscal.pesoBruto"
              decimalScale={6}
              isAllowed={maximo9DigitosEsquerda}
              decimalSeparator=","
              allowNegative={false}
              value={formik.values?.fiscal?.pesoBruto}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              touched={formik.touched.fiscal?.pesoBruto}
              error={formik.errors.fiscal?.pesoBruto}
              warning={warnPesoBruto ? 'Confirme se o peso bruto é realmente menor que o peso líquido' : false}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={4} lg={4} xl={4}>
            <NumberInput
              label="Altura (cm)"
              id="fiscal.altura"
              decimalScale={6}
              isAllowed={maximo9DigitosEsquerda}
              decimalSeparator=","
              allowNegative={false}
              pattern={onlyNumberRegex}
              value={formik.values?.fiscal?.altura}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              touched={formik.touched.fiscal?.altura}
              error={formik.errors.fiscal?.altura}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={4} lg={4} xl={4}>
            <NumberInput
              label="Largura (cm)"
              id="fiscal.largura"
              decimalScale={6}
              isAllowed={maximo9DigitosEsquerda}
              decimalSeparator=","
              allowNegative={false}
              pattern={onlyNumberRegex}
              value={formik.values?.fiscal?.largura}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              touched={formik.touched.fiscal?.largura}
              error={formik.errors.fiscal?.largura}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={4} lg={4} xl={4}>
            <NumberInput
              label="Profundidade (cm)"
              id="fiscal.profundidade"
              decimalScale={6}
              isAllowed={maximo9DigitosEsquerda}
              decimalSeparator=","
              allowNegative={false}
              pattern={onlyNumberRegex}
              value={formik.values?.fiscal?.profundidade}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              touched={formik.touched.fiscal?.profundidade}
              error={formik.errors.fiscal?.profundidade}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <SelectAsyncInput
              id="fiscal.origemProduto"
              name="fiscal.origemProduto"
              label="Origem do produto"
              value={formik.values?.fiscal?.origemProduto}
              onBlur={formik.handleBlur}
              error={_errors?.fiscal?.origemProduto}
              touched={!!_errors?.fiscal?.origemProduto}
              fetchOnFocus
              required={formik.values?.fiscal?.icms?.possuiIcms}
              onChange={(event, value) => {
                formik.setFieldValue('fiscal.origemProduto.id', value?.id);
                formik.setFieldValue('fiscal.origemProduto.valor', value?.valor);
                formik.setFieldValue('fiscal.origemProduto.descricao', value?.descricao);
              }}
              getOptionKey={(option) => {
                return option?.id;
              }}
              getOptionLabel={(option) => {
                return option?.valor && option?.descricao ? `${option.valor} - ${option.descricao}` : '';
              }}
              fetchData={async (query = '', next = 0) => {
                if (typeof query === 'object') {
                  query = '';
                }
                const response = await origemProdutoApi.search({
                  size: 100,
                  sort: 'valor',
                  page: next,
                  q: query,
                });

                const data = response?.data || [];

                return { data };
              }}
              renderOption={(option) => {
                const labelOption = option?.valor && option?.descricao ? `${option.valor} - ${option.descricao}` : '';
                return (
                  <Tooltip title={labelOption}>
                    <div className={styles.container} style={{ maxWidth: '100%' }}>
                      {labelOption}
                    </div>
                  </Tooltip>
                );
              }}
            />
          </Grid>
        </>
      ) : (
        <>
          <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
            <SelectAsyncInput
              id="fiscal.cfop"
              name="fiscal.cfop"
              label="CFOP"
              value={formik.values?.fiscal?.cfop}
              fetchOnFocus
              onChange={(event, value) => {
                formik.setFieldValue('fiscal.cfop.id', value?.id);
                formik.setFieldValue('fiscal.cfop.codigo', value?.codigo);
                formik.setFieldValue('fiscal.cfop.descricao', value?.descricao);
              }}
              getOptionKey={(option) => {
                return option?.id;
              }}
              getOptionLabel={(option) => {
                return option?.codigo && option?.descricao ? `${option.codigo} - ${option.descricao}` : '';
              }}
              fetchData={async (query = '', next = 0) => {
                if (typeof query === 'object') {
                  query = '';
                }
                const response = await cfopApi.search({
                  size: 100,
                  sort: 'codigo',
                  page: next,
                  q: query,
                });

                const data = response?.data || [];

                return { data };
              }}
              renderOption={(option) => {
                const labelOption = option?.codigo && option?.descricao ? `${option.codigo} - ${option.descricao}` : '';
                return (
                  <Tooltip title={labelOption}>
                    <div className={styles.container} style={{ maxWidth: '100%' }}>
                      {labelOption}
                    </div>
                  </Tooltip>
                );
              }}
            />
          </Grid>
          <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
            <SelectAsyncInput
              id="fiscal.origemProduto"
              name="fiscal.origemProduto"
              label="Origem do produto"
              value={formik.values?.fiscal?.origemProduto}
              onBlur={formik.handleBlur}
              error={_errors?.fiscal?.origemProduto}
              touched={!!_errors?.fiscal?.origemProduto}
              fetchOnFocus
              required={formik.values?.fiscal?.icms?.possuiIcms}
              onChange={(event, value) => {
                formik.setFieldValue('fiscal.origemProduto.id', value?.id);
                formik.setFieldValue('fiscal.origemProduto.valor', value?.valor);
                formik.setFieldValue('fiscal.origemProduto.descricao', value?.descricao);
              }}
              getOptionKey={(option) => {
                return option?.id;
              }}
              getOptionLabel={(option) => {
                return option?.valor && option?.descricao ? `${option.valor} - ${option.descricao}` : '';
              }}
              fetchData={async (query = '', next = 0) => {
                if (typeof query === 'object') {
                  query = '';
                }
                const response = await origemProdutoApi.search({
                  size: 100,
                  sort: 'valor',
                  page: next,
                  q: query,
                });

                const data = response?.data || [];

                return { data };
              }}
              renderOption={(option) => {
                const labelOption = option?.valor && option?.descricao ? `${option.valor} - ${option.descricao}` : '';
                return (
                  <Tooltip title={labelOption}>
                    <div className={styles.container} style={{ maxWidth: '100%' }}>
                      {labelOption}
                    </div>
                  </Tooltip>
                );
              }}
            />
          </Grid>
        </>
      )}

      {isAtualizarAliquota && (
        <>
          <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
            <NumberInput
              label="Número do pedido de compra (xPed)"
              id="fiscal.numeroPedidoCompra"
              decimalScale={6}
              isAllowed={maximo10DigitosEsquerda}
              decimalSeparator={false}
              allowNegative={false}
              pattern={onlyNumberRegex}
              value={formik.values?.fiscal?.numeroPedidoCompra}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              touched={formik.touched.fiscal?.numeroPedidoCompra}
              error={formik.errors.fiscal?.numeroPedidoCompra}
            />
          </Grid>
          <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
            <NumberInput
              label="Item do pedido de compra (nItemPed)"
              id="fiscal.numeroItemPedidoCompra"
              decimalScale={6}
              isAllowed={maximo10DigitosEsquerda}
              decimalSeparator={false}
              allowNegative={false}
              pattern={onlyNumberRegex}
              value={formik.values?.fiscal?.numeroItemPedidoCompra}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              touched={formik.touched.fiscal?.numeroItemPedidoCompra}
              error={formik.errors.fiscal?.numeroItemPedidoCompra}
            />
          </Grid>
        </>
      )}

      <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
        {!esconderCamposIcms ? <DadosFiscaisIcms name="fiscal.icms" /> : null}
        <DadosFiscaisIpi name="fiscal.ipi" />
      </Grid>

      {empresaIbsCbs && (
        <>
          <Grid item xs={12}>
            <Typography variant="body1" style={{ fontWeight: 'bold' }}>
              IBS / CBS - Reforma Tributária
            </Typography>
            <Divider />
          </Grid>

          <Grid item xs={6}>
            <SelectCstSituacaoTributaria
              id="fiscal.cstSituacaoTributaria"
              name="fiscal.cstSituacaoTributaria"
              value={formik.values?.fiscal?.cstSituacaoTributaria}
              onChange={(e) => {
                formik.handleChange(e);
                formik.setFieldValue('fiscal.cstClassificacaoTributaria', null);
                formik.setFieldValue('fiscal.cstSituacaoTributaria.idSituacao', e.target.value?.idSituacao);
              }}
              onBlur={formik.handleBlur}
              label="Código de situação tributária (CST)"
              touched={!!_errors?.fiscal?.cstSituacaoTributaria}
              error={_errors?.fiscal?.cstSituacaoTributaria}
              required={isVenda ? false : true}
            />
          </Grid>
          <Grid item xs={6}>
            {campoCstClassificacaoTributaria()}
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
            <NumberInput
              id="fiscal.valorIbs"
              label="IBS (%)"
              decimalScale={8}
              decimalSeparator=","
              allowNegative={false}
              isAllowed={(values) => {
                return !values.floatValue || values.floatValue <= 100;
              }}
              value={formik.values.fiscal?.valorIbs}
              onChange={(event) => {
                formik.handleChange(event);
                if (event.target.value === undefined) {
                  formik.setFieldValue('fiscal.ibsRetidoNaFonte', false);
                }
              }}
              onBlur={formik.handleBlur}
              touched={!!_errors?.fiscal?.valorIbs}
              error={_errors?.fiscal?.valorIbs}
              required={isVenda ? false : true}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
            <NumberInput
              id="fiscal.valorCbs"
              label="CBS (%)"
              decimalScale={8}
              decimalSeparator=","
              allowNegative={false}
              isAllowed={(values) => {
                return !values.floatValue || values.floatValue <= 100;
              }}
              value={formik.values.fiscal?.valorCbs}
              onChange={(event) => {
                formik.handleChange(event);
                if (event.target.value === undefined) {
                  formik.setFieldValue('fiscal.cbsRetidoNaFonte', false);
                }
              }}
              onBlur={formik.handleBlur}
              touched={!!_errors?.fiscal?.valorCbs}
              error={_errors?.fiscal?.valorCbs}
              required={isVenda ? false : true}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
            <NumberInput
              id="fiscal.valorIs"
              label="IS (%)"
              decimalScale={8}
              decimalSeparator=","
              allowNegative={false}
              isAllowed={(values) => {
                return !values.floatValue || values.floatValue <= 100;
              }}
              value={formik.values.fiscal?.valorIs}
              onChange={(event) => {
                formik.handleChange(event);
                if (event.target.value === undefined) {
                  formik.setFieldValue('fiscal.isRetidoNaFonte', false);
                }
              }}
              onBlur={formik.handleBlur}
              touched={!!_errors?.fiscal?.valorIs}
              error={_errors?.fiscal?.valorIs}
              required={isVenda ? false : true}
            />
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default TabDadosFiscaisProduto;
